import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

import { format, toZonedTime } from 'date-fns-tz';

import { digitsOnly, getSafe, isNullOrWhitespace } from 'helpers';

import { ShiftTime } from 'generated/graphql';

@Component({
  selector: 'app-shift-day-line',
  templateUrl: './shift-day-line.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShiftDayLineComponent {
  @Input() day: DayOfWeek;
  @Input() shiftTimes: ShiftTime[];
  @ViewChild(MatTooltip, { static: false }) tooltip;

  /**
   *
   */
  constructor() {
  }

  // used for labels
  // and other caluclations
  dayInfo = {
    monday: {
      abbr: 'M',
      jsDay: 1
    },
    tuesday: { abbr: 'T', jsDay: 2 },
    wednesday: { abbr: 'W', jsDay: 3 },
    thursday: { abbr: 'T', jsDay: 4 },
    friday: { abbr: 'F', jsDay: 5 },
    saturday: { abbr: 'S', jsDay: 6 },
    sunday: { abbr: 'S', jsDay: 0 }
  };

  get isToday() {
    return this.dayInfo[this.day].jsDay === new Date().getDay();
  }

  get now() {
    return new Date();
  }

  get shiftTimezone() {
    return getSafe(() => this.shiftTimes[0].timezone, null);
  }

  /**
   * The shift info for the current day
   */
  get shiftInfos() {
    return (this.shiftTimes || []).filter(st => st.dayOfWeek === this.day);
  }

  /**
   * Time relative to the timezone set by the shift
   */
  get timeWhereShiftIs() {
    if (!this.shiftTimezone) {
      return null;
    }
    const result = format(toZonedTime(this.now.toISOString(), this.shiftTimezone), 'hh:mm a');
    return result;
  }

  /**
   * Returns shift formatted
   * 09:00AM - 05:00PM
   */
  shiftTimeString(shiftTime: ShiftTime) {
    if (!shiftTime) {
      return;
    }

    const { startTime, endTime } = shiftTime;
    const ts = `${this.parse24HourTime(startTime).join(' ')} - ${this.parse24HourTime(endTime).join(' ')}`;

    return ts.replace(/ /g, '');
  }

  // NOTE:
  // For the shift visualization, let's add a class to the div.day__shift where if the shift is < 8 hours.
  // Then if the shift starts before 4am, or ends after 8pm.
  // .day__shift--short, day__shift--early, and .day__shift--late
  // we only need the --early or --late one if --short is present

  shiftIsShort(shiftTime: ShiftTime) {
    const getTotalMinutes = (hours: number, minutes: number) => (hours * 60) + minutes;

    if (!shiftTime) {
      return false;
    }

    const { startTime, endTime } = shiftTime;
    if (!startTime || !endTime) {
      return false;
    }

    const [startHour, startMinutes] = startTime.split(':').map(sn => parseInt(sn, 10));
    const [endHour, endMinutes] = endTime.split(':').map(sn => parseInt(sn, 10));

    const minutesOfDayPassedAtStartOfShift = getTotalMinutes(startHour, startMinutes);
    const minutesOfDayPassedAtEndOfShift = getTotalMinutes(endHour, endMinutes);

    const totalMinutesPassed = minutesOfDayPassedAtEndOfShift - minutesOfDayPassedAtStartOfShift;

    return totalMinutesPassed < 480;
  }

  shiftStartsEarly(shiftTime: ShiftTime) {
    if (!shiftTime) {
      return false;
    }

    const { startTime } = shiftTime;
    if (!startTime) {
      return false;
    }

    const [startHour] = startTime.split(':').map(sn => parseInt(sn, 10));

    return startHour <= 4;
  }

  shiftEndsLate(shiftTime: ShiftTime) {
    if (!shiftTime) {
      return false;
    }

    const { endTime } = shiftTime;
    if (!endTime) {
      return false;
    }

    const [endHour] = endTime.split(':').map(sn => parseInt(sn, 10));

    return endHour >= 20;
  }

  get currentTimeStyle() {
    //  No time zone set, use local time
    if (!this.shiftTimezone) {
      const localHour = this.now.getHours();
      // handles case where minute is a single diget
      let localMinute = `${this.now.getMinutes()}`;
      if (localMinute.length === 1) {
        localMinute = `0${localMinute}`;
      }
      const hourMinutes = `${localHour}${localMinute}`;
      // Percentage of the day that has elapased
      const dayPassedPercentage = (parseInt(hourMinutes, 10) / 2400) * 100;
      return { left: `${dayPassedPercentage}%` };
    }

    // Use the timezone of shift to display the current time style
    const [shiftHour, shiftMinute] = format(toZonedTime(this.now.toISOString(), this.shiftTimezone), 'HH:mm').split(':');
    const shiftHourMinutes = `${shiftHour}${shiftMinute}`;
    const shiftDayPassedPercentage = (parseInt(shiftHourMinutes, 10) / 2400) * 100;
    return { left: `${shiftDayPassedPercentage}%` };
  }

  fillStyle(shiftTime: ShiftTime) {
    if (!shiftTime || !shiftTime.startTime || !shiftTime.endTime) {
      return {};
    }
    // Sample shift 0900 - 1700
    // =800
    //  800 / 2400
    //  0.3334 * 100 = 33.34 (duration (width))
    //  0900 / 2400 (start (left))

    const startTime = shiftTime.startTime; // 08:00
    const endTime = shiftTime.endTime; // 13:00
    const startTimeNumeric = digitsOnly(startTime);
    const endTimeNumeric = digitsOnly(endTime);
    const start = startTimeNumeric / 2400;
    const duration = Math.abs(startTimeNumeric - endTimeNumeric) / 2400;

    const styles = {
      left: `${start * 100}%`,
      width: `${duration * 100}%`
    };

    return styles;
  }

  /**
   * If time is formatted as 13:00. Will return ['1:00', 'p m']
   * includes spaces for css formatting
   */
  parse24HourTime(time: string): [string, 'a m' | 'p m'] {
    let amPm: 'a m' | 'p m' = null;
    if (isNullOrWhitespace(time)) {
      return [null, null];
    }
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    if (hour >= 12) {
      amPm = 'p m';
    } else {
      amPm = 'a m';
    }

    // 24h and 12h mod 12 === 0, in which case 12 is needed.
    const timePart = `${hour % 12 || 12}:${minutes}`;

    return [timePart, amPm];
  }

}
